import {
  MessarioAiFetchFailure,
  requestMessarioAiGenerate
} from '@lib/messarioAi/api';
import {
  clearMessarioAiGenerateBundleCache,
  ensureMessarioAiGenerateBundle
} from '@lib/messarioAi/generateBundleCache';
import {
  MessarioAiBundleEnabled,
  MessarioAiContextMessage,
  MessarioAiDraftResponse,
  MessarioAiGenerateBundleEnabled,
  MessarioAiGenerateDraftPayload
} from '@lib/messarioAi/types';

type GenerateArgs = {
  token: string,
  panelOrigin: string,
  externalUserId: string,
  peerId: string,
  messages: MessarioAiContextMessage[],
  oneShot?: string,
  metadata: MessarioAiBundleEnabled,
  fetchValue?: typeof fetch,
  storage?: Storage
};

export function isOutdatedAiBundleFailure(err: unknown): boolean {
  if(!(err instanceof MessarioAiFetchFailure) || err.errorCode === 'attempt_in_progress') {
    return false;
  }

  return err.errorCode === 'outdated_ai_bundle' ||
    err.errorCode === 'configuration_conflict' || err.errorCode === 'configuration_format_changed';
}

function newClientAttemptId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if(uuid) {
    return uuid;
  }

  return `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`.slice(0, 64);
}

function buildGeneratePayload(
  peerId: string,
  messages: MessarioAiContextMessage[],
  oneShot: string | undefined,
  bundle: MessarioAiGenerateBundleEnabled,
  clientAttemptId: string
): MessarioAiGenerateDraftPayload {
  const payload: MessarioAiGenerateDraftPayload = {
    config_schema_version: bundle.config_schema_version,
    peer_id: peerId,
    messages,
    prompt_revision: bundle.prompt_revision,
    variable_definitions_revision: bundle.variable_definitions_revision,
    model: bundle.model,
    client_attempt_id: clientAttemptId
  };
  if(oneShot) {
    payload.one_shot_instruction = oneShot;
  }

  return payload;
}

export async function generateMessarioAiDraft(args: GenerateArgs): Promise<MessarioAiDraftResponse> {
  const fetchValue = args.fetchValue ?? fetch;
  const storage = args.storage ?? window.sessionStorage;
  const bundle = await ensureMessarioAiGenerateBundle(args.token, args.panelOrigin, args.externalUserId, args.metadata, fetchValue, storage);
  const payload = buildGeneratePayload(args.peerId, args.messages, args.oneShot, bundle, '');
  const identity = JSON.stringify(payload);
  const key = `messario_ai_attempt:${args.externalUserId}:${args.peerId}`;
  let attempt: {identity: string, id: string};
  try { attempt = JSON.parse(storage.getItem(key) || 'null'); } catch{}
  if(!attempt || attempt.identity !== identity) attempt = {identity, id: newClientAttemptId()};
  storage.setItem(key, JSON.stringify(attempt));
  payload.client_attempt_id = attempt.id;
  try {
    const result = await requestMessarioAiGenerate(args.token, args.panelOrigin, payload, fetchValue);
    storage.removeItem(key);
    return result;
  } catch(err) {
    if(isOutdatedAiBundleFailure(err)) {
      clearMessarioAiGenerateBundleCache(args.externalUserId, storage);
      storage.removeItem(key);
    }
    throw err;
  }
}
